import { motion } from "framer-motion";

interface CtaButtonProps {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
  variant?: "primary" | "outline";
  type?: "button" | "submit";
  external?: boolean;
  onPinkBg?: boolean;
  onDarkBg?: boolean;
}

const CtaButton = ({ children, href, onClick, variant = "primary", type = "button", external, onPinkBg, onDarkBg }: CtaButtonProps) => {
  const baseClasses =
    "relative inline-flex items-center justify-center text-xs tracking-[0.2em] uppercase font-body px-10 py-4 overflow-hidden group transition-all duration-500";

  const variants = {
    primary:
      "bg-primary text-primary-foreground border border-primary hover:shadow-lg",
    outline: onDarkBg
      ? "bg-transparent text-primary-foreground border border-primary-foreground/60 hover:border-primary-foreground hover:shadow-lg"
      : "bg-transparent text-foreground border border-foreground/30 hover:border-foreground hover:shadow-lg",
  };

  const shimmerColor = variant === "primary" || onPinkBg || onDarkBg ? "via-white/20" : "via-secondary/40";

  const inner = (
    <>
      {/* Shimmer sweep on hover */}
      <span className={`absolute inset-0 bg-gradient-to-r from-transparent ${shimmerColor} to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700 ease-in-out`} />
      {/* Text with subtle lift */}
      <span className="relative z-10 group-hover:-translate-y-px transition-transform duration-300">
        {children}
      </span>
    </>
  );

  if (href) {
    return (
      <motion.a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className={`${baseClasses} ${variants[variant]}`}
      >
        {inner}
      </motion.a>
    );
  }

  return (
    <motion.button
      type={type}
      onClick={onClick}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      className={`${baseClasses} ${variants[variant]}`}
    >
      {inner}
    </motion.button>
  );
};

export default CtaButton;
